import React, { useState } from 'react';
import { DateTime } from 'luxon';
import { Fade, Tooltip } from '@material-ui/core';
import { createStyles, WithStyles, withStyles } from '@material-ui/core/styles';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useHistory } from 'react-router-dom';

import { useIsNewInvoicingFlow } from 'src/components/NewInvoicing/hooks/useIsNewInvoicingFlow';

import { CoachingVisitState } from '../../../../../../generated/webapp_gql';
import MHButton from '../../../../../MHButton';
import { ReactComponent as CheckIcon } from '../../../../../../assets/images/check-icon.svg';
import { ReactComponent as CopyIcon } from '../../../../../../assets/images/copy-icon.svg';
import { ReactComponent as InfoIcon } from '../../../../../../assets/images/info-icon.svg';
import { CurrentUser } from '../../../../../../rematch-models/user';
import { FormattedCoachingVisit } from '../../../../../../models/Sessions';
import { colors } from '../../../../../../themes/colors';
import { DEFAULT_BOX_SHADOW } from '../../../../../../themes';
import { buildVideoSessionLink } from './helpers';
import {
  SessionActionsTestID,
  DEFAULT_CANCELABLE_SESSION_STATES,
  HPI_CANCELABLE_SESSION_STATES,
  POST_SESSION_REJOIN_VIDEO_BUFFER_TIME,
} from './constants';
import { VisitType } from '../SessionType/VisitTypeChip/constants';

const styles = (theme) =>
  createStyles({
    rowButtonContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      flexWrap: 'wrap',
      alignItems: 'center',
      padding: '0 10px 0 20px',
    },
    cancelButton: {
      margin: '5px 10px 5px 0',
      padding: '6px 12px',
      color: colors.black7,
      borderColor: colors.gray16,
      '&:hover': {
        backgroundColor: colors.white1,
        borderColor: colors.gray16,
      },
    },
    reportVisitButton: {
      display: 'flex',
      margin: '5px 10px 5px 0',
      padding: '7px 10px',
      backgroundColor: colors.turquoise1,
      color: 'white',
      '&:hover': {
        backgroundColor: colors.turquoise1,
        color: 'white',
        boxShadow: DEFAULT_BOX_SHADOW,
      },
    },
    editVisitButton: {
      display: 'flex',
      margin: '5px 10px 5px 0',
      padding: '3px 18px',
      backgroundColor: colors.black2,
      color: 'white',
      borderRadius: '22px',
      '&:hover': {
        backgroundColor: colors.black2,
        color: 'white',
        boxShadow: DEFAULT_BOX_SHADOW,
      },
    },
    buttonIcon: {
      marginRight: 10,
    },
    sessionButtonsContainer: {
      display: 'flex',
      flexWrap: 'nowrap',
      justifyContent: 'center',
      alignItems: 'center',
      margin: '5px 10px 5px 0',
    },
    copySessionLinkButton: {
      position: 'relative',
      display: 'flex',
      flex: 0,
      width: 42,
      minWidth: 42,
      height: 38,
      padding: 0,
      backgroundColor: colors.blue6,
      color: 'white',
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },
    joinSessionButton: {
      display: 'flex',
      marginLeft: 2,
      padding: '7px 14px',
      flex: 0,
      backgroundColor: colors.blue6,
      color: 'white',
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
    tooltipIcon: {
      width: 32,
      height: 20,
      marginRight: 12,
    },
  });

const HtmlTooltip = withStyles(() => ({
  tooltip: {
    transform: 'translate3d(0, 0, 0) !important',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    maxWidth: 370,
    padding: 12,
    fontSize: '14px',
    fontWeight: 800,
    backgroundColor: colors.black2,
    color: colors.white1,
    borderRadius: 4,
  },
}))(Tooltip);

export interface SessionActionsProps {
  currentUser: CurrentUser;
  isUpcomingView: boolean;
  onJoinSession: () => void;
  setReportVisitModalOpen: (boolean) => void;
  setShowCancelSessionBookedModal: (boolean) => void;
  visit: FormattedCoachingVisit;
}

type Props = WithStyles<typeof styles> & SessionActionsProps;

const SessionActions: React.FC<Props> = ({
  classes,
  currentUser,
  isUpcomingView,
  onJoinSession,
  setReportVisitModalOpen,
  setShowCancelSessionBookedModal,
  visit,
}) => {
  const [showCopiedLinkSuccess, setShowCopiedLinkSuccess] = useState(false);

  const { showHpiClientStatusInfo } = useFlags();
  const isNewInvoicingFlow = useIsNewInvoicingFlow();
  const history = useHistory();

  const copyVideoSessionLink = () => {
    const link = buildVideoSessionLink(visit?.uuid);

    if (navigator) {
      navigator.clipboard.writeText(link);
      setShowCopiedLinkSuccess(true);
      setTimeout(() => {
        setShowCopiedLinkSuccess(false);
      }, 3000);
    }
  };

  const scheduledVisitEndTimeWithBuffer = visit.scheduledDateTime
    .setZone(currentUser.localTimezone)
    .plus({ minutes: visit.scheduledDuration })
    .plus({ minutes: POST_SESSION_REJOIN_VIDEO_BUFFER_TIME });

  const startTimeInSec = visit.scheduledDateTime.toMillis();
  const nowInSec = DateTime.utc().toMillis();

  const isCancelledHPISession =
    visit.visitType === VisitType.HealthPlan && visit?.state === CoachingVisitState.Cancelled;
  const isPendingPaymentHPISession =
    visit.visitType === VisitType.HealthPlan && visit?.state === CoachingVisitState.PendingPayment;
  const shouldShowReportButton =
    startTimeInSec <= nowInSec && !isCancelledHPISession && !isPendingPaymentHPISession;

  const shouldShowJoinSessionButton =
    visit &&
    visit?.state === CoachingVisitState.Scheduled &&
    DateTime.local().setZone(currentUser.localTimezone) < scheduledVisitEndTimeWithBuffer;

  const shouldShowCancelSessionButton =
    isUpcomingView &&
    visit &&
    (DEFAULT_CANCELABLE_SESSION_STATES.includes(visit.state) ||
      (showHpiClientStatusInfo && HPI_CANCELABLE_SESSION_STATES.includes(visit.state)));

  return (
    <div className={classes.rowButtonContainer}>
      {shouldShowCancelSessionButton && (
        <MHButton
          data-testid={SessionActionsTestID.CancelVisitButton}
          extraClasses={[classes.cancelButton]}
          onClick={() => setShowCancelSessionBookedModal(true)}
          variant="outlined"
        >
          Cancel session
        </MHButton>
      )}

      {shouldShowJoinSessionButton && (
        <div className={classes.sessionButtonsContainer}>
          <HtmlTooltip
            arrow
            placement="top"
            title={
              <>
                <InfoIcon className={classes.tooltipIcon} />
                <p>
                  As a back up, copy and send this link to your client so they have access to the
                  session.
                </p>
              </>
            }
            TransitionComponent={Fade}
            TransitionProps={{ timeout: 600 }}
          >
            <div>
              <MHButton
                data-testid={SessionActionsTestID.CopyVideoSessionLinkButton}
                className={classes.copySessionLinkButton}
                onClick={copyVideoSessionLink}
              >
                {showCopiedLinkSuccess && (
                  <CheckIcon data-testid={SessionActionsTestID.CopySessionBtnCheckIcon} />
                )}

                {!showCopiedLinkSuccess && (
                  <CopyIcon data-testid={SessionActionsTestID.CopySessionBtnCopyIcon} />
                )}
              </MHButton>
            </div>
          </HtmlTooltip>

          <MHButton
            data-testid={SessionActionsTestID.JoinVideoSessionButton}
            extraClasses={[classes.joinSessionButton]}
            onClick={onJoinSession}
          >
            Join session
          </MHButton>
        </div>
      )}

      {shouldShowReportButton && (
        <MHButton
          data-testid={SessionActionsTestID.EditReportVisitButton}
          extraClasses={[
            visit?.state === CoachingVisitState.Scheduled
              ? classes.reportVisitButton
              : classes.editVisitButton,
          ]}
          onClick={(e: Event) => {
            e.stopPropagation();
            if (isNewInvoicingFlow) history.push(`/invoicing?visitUuid=${visit.uuid}`);
            else setReportVisitModalOpen(true);
          }}
        >
          {visit?.state !== CoachingVisitState.Scheduled && (
            <CheckIcon className={classes.buttonIcon} />
          )}
          {visit?.state === CoachingVisitState.Scheduled ? 'Submit invoice' : 'Submitted'}
        </MHButton>
      )}
    </div>
  );
};

export default withStyles(styles)(SessionActions);
